import React, { useState, useEffect } from 'react';
import { DollarSign, Building2, FileText, Settings, Plus, Edit, Check, X, Search, RefreshCw, Download, AlertCircle, Users, Trash2, Clock, Play, Pause, Gift, UserCircle, MapPin, Phone, Mail } from 'lucide-react';
import { api } from '../api/client';

const CURRENCY_SYMBOLS = { ZAR: 'R', USD: '$', EUR: '€', GBP: '£' };

const formatCurrency = (amount, currency) => {
  if (amount === null || amount === undefined) return 'Custom';
  const symbol = CURRENCY_SYMBOLS[currency] || currency;
  return symbol + amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

// Feature labels for display
const FEATURE_LABELS = {
  portfolio_view: 'Portfolio View',
  program_view: 'Program View',
  project_view: 'Project View',
  epic_view: 'Epic View',
  feature_view: 'Feature View',
  story_view: 'Story View',
  task_view: 'Task View',
  risk_view: 'Risk View',
  issue_tracking: 'Issue Tracking',
  dependency_tracking: 'Dependencies',
  decision_tracking: 'Decisions',
  exec_dash: 'Executive Dashboard',
  pmo_dash: 'PMO Dashboard',
  pm_dash: 'PM Dashboard',
  upload_center: 'Upload Center',
  manual_editing: 'Manual Editing',
  roadmap_view: 'Roadmap View',
  roadmap_editing: 'Roadmap Editing',
  daily_signal: 'Daily Signal™',
  team_management: 'Team Management',
  sprint_planning: 'Sprint Planning',
  reports: 'Reports',
  export_data: 'Export Data',
  bulk_upload: 'Bulk Upload',
  api_access: 'API Access',
  sso_integration: 'SSO/SCIM',
  integrations: 'Integrations (ADO/Jira)',
  audit_logs: 'Audit Logs',
  versioning: 'Versioning',
  steerco_pack: 'Steerco Pack Generator',
  custom_branding: 'Custom Branding',
  compliance_mode: 'Compliance Mode',
  // Consulting Suite
  consulting_suite: 'Consulting Suite',
  proposal_builder: 'Proposal Builder',
  planning_step: 'Planning Step',
  rate_card: 'Rate Card Management',
  templates_pack: 'Templates Pack',
  convert_to_project: 'Convert to Project',
  rfp_mode: 'RFP Response Mode',
  scopeops_governance: 'ScopeOps Governance'
};

const BillingAdmin = () => {
  const [activeTab, setActiveTab] = useState('catalog');
  const [loading, setLoading] = useState(true);
  const [catalog, setCatalog] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [selectedCurrency, setSelectedCurrency] = useState('ZAR');
  const [editingTier, setEditingTier] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [tenantSearch, setTenantSearch] = useState('');
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [tenantBilling, setTenantBilling] = useState(null);
  const [tenantTrial, setTenantTrial] = useState(null);
  const [showEditBilling, setShowEditBilling] = useState(false);
  const [showCreateTenant, setShowCreateTenant] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [showCreateInvoice, setShowCreateInvoice] = useState(false);
  const [showStartTrial, setShowStartTrial] = useState(false);
  const [trialConfig, setTrialConfig] = useState(null);
  const [trials, setTrials] = useState([]);
  // Client Management State
  const [clients, setClients] = useState([]);
  const [clientSearch, setClientSearch] = useState('');
  const [selectedClient, setSelectedClient] = useState(null);
  const [showEditClient, setShowEditClient] = useState(false);
  const [showCreateClient, setShowCreateClient] = useState(false);

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    if (activeTab === 'tenants') loadTenants();
    else if (activeTab === 'invoices') loadInvoices();
    else if (activeTab === 'trials') loadTrials();
    else if (activeTab === 'clients') loadClients('');
  }, [activeTab]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      const [catalogRes, currenciesRes, trialRes] = await Promise.all([
        api.getPricingCatalog(),
        api.getSupportedCurrencies(),
        api.getTrialConfig().catch(() => ({ data: null }))
      ]);
      setCatalog(catalogRes.data.catalog || []);
      setCurrencies(currenciesRes.data.supported || ['ZAR', 'USD', 'EUR', 'GBP']);
      if (trialRes.data) setTrialConfig(trialRes.data.config);
    } catch (error) {
      console.error('Failed to load catalog:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadTenants = async () => {
    try {
      const res = await api.getTenants({ search: tenantSearch });
      setTenants(res.data.tenants || []);
    } catch (error) {
      console.error('Failed to load tenants:', error);
    }
  };

  const loadTenantBilling = async (tenantId) => {
    try {
      const [billingRes, trialRes] = await Promise.all([
        api.getTenantBilling(tenantId),
        api.getTenantTrial(tenantId).catch(() => ({ data: { has_trial: false } }))
      ]);
      setTenantBilling(billingRes.data);
      setTenantTrial(trialRes.data);
      const tenant = tenants.find(t => t.id === tenantId);
      setSelectedTenant(tenant);
    } catch (error) {
      console.error('Failed to load tenant billing:', error);
    }
  };

  const loadInvoices = async () => {
    try {
      const res = await api.getInvoices();
      setInvoices(res.data.invoices || []);
    } catch (error) {
      console.error('Failed to load invoices:', error);
    }
  };

  const loadTrials = async () => {
    try {
      const res = await api.listTrials();
      setTrials(res.data.trials || []);
    } catch (error) {
      console.error('Failed to load trials:', error);
    }
  };

  // Client Management Functions
  const loadClients = async (searchTerm = '') => {
    try {
      const params = {};
      if (searchTerm) params.search = searchTerm;
      const res = await api.getClients(params);
      setClients(res.data.clients || []);
    } catch (error) {
      console.error('Failed to load clients:', error);
    }
  };

  const handleCreateClient = async (clientData) => {
    try {
      await api.createClient(clientData);
      await loadClients('');
      setShowCreateClient(false);
      alert('Client created successfully!');
    } catch (error) {
      alert('Failed to create client: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleUpdateClient = async (clientData) => {
    if (!selectedClient) return;
    try {
      await api.updateClient(selectedClient.id, clientData);
      await loadClients('');
      setShowEditClient(false);
      setSelectedClient(null);
      alert('Client updated successfully!');
    } catch (error) {
      alert('Failed to update client: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleDeleteClient = async (clientId) => {
    if (!confirm('Are you sure you want to delete this client?')) return;
    try {
      await api.deleteClient(clientId);
      await loadClients('');
      if (selectedClient && selectedClient.id === clientId) {
        setSelectedClient(null);
      }
      alert('Client deleted successfully!');
    } catch (error) {
      alert('Failed to delete client: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleStartTrial = async (tenantId, durationDays) => {
    try {
      await api.startTrial(tenantId, { duration_days: durationDays });
      await loadTenantBilling(tenantId);
      setShowStartTrial(false);
      alert('Trial started successfully!');
    } catch (error) {
      alert('Failed to start trial: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleEndTrial = async (tenantId) => {
    if (!confirm('Are you sure you want to end this trial? The tenant will be downgraded to Starter tier.')) return;
    try {
      await api.endTrial(tenantId, { action: 'downgrade_to_starter', reason: 'admin_action' });
      await loadTenantBilling(tenantId);
      alert('Trial ended. Tenant downgraded to Starter.');
    } catch (error) {
      alert('Failed to end trial: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleSavePricing = async (tierData) => {
    try {
      await api.upsertPricing({
        currency: selectedCurrency,
        tier: tierData.tier,
        price: tierData.price,
        is_active: true,
        features: tierData.features,
        limits: tierData.limits
      });
      await loadInitialData();
      setEditingTier(null);
    } catch (error) {
      alert('Failed to save pricing: ' + error.message);
    }
  };

  const handleCreateTenant = async (tenantData) => {
    try {
      await api.createTenant(tenantData);
      await loadTenants();
      setShowCreateTenant(false);
    } catch (error) {
      alert('Failed to create tenant: ' + error.message);
    }
  };

  const handleUpdateBilling = async (billingData) => {
    if (!selectedTenant) return;
    try {
      await api.updateTenantBilling(selectedTenant.id, billingData);
      await loadTenantBilling(selectedTenant.id);
      setShowEditBilling(false);
    } catch (error) {
      alert('Failed to update billing: ' + error.message);
    }
  };

  const handleCreateInvoice = async (tenantId) => {
    try {
      await api.createInvoice({ tenant_id: tenantId });
      loadInvoices();
      setShowCreateInvoice(false);
    } catch (error) {
      alert('Failed to create invoice: ' + error.message);
    }
  };

  const handleUpdateInvoiceStatus = async (invoiceId, status) => {
    try {
      await api.updateInvoice(invoiceId, { status });
      loadInvoices();
    } catch (error) {
      alert('Failed to update invoice: ' + error.message);
    }
  };

  const getCurrentCurrencyCatalog = () => {
    return catalog.find(c => c.currency === selectedCurrency) || { tiers: [] };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--clr-brand-primary)]"></div>
      </div>
    );
  }

  const currentCatalog = getCurrentCurrencyCatalog();

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-[var(--clr-text-primary)] flex items-center gap-3">
          <DollarSign className="w-7 h-7 text-[var(--clr-sev-healthy)]" />
          Billing & Pricing Administration
        </h1>
        <p className="text-[var(--clr-text-secondary)] mt-1">Manage pricing catalog, tenants, billing settings, and invoices</p>
      </div>

      <div className="border-b">
        <div className="flex gap-6">
          <button
            onClick={() => setActiveTab('catalog')}
            className={`flex items-center gap-2 pb-3 px-1 text-sm font-medium border-b-2 transition-colors ${activeTab === 'catalog' ? 'border-green-600 text-[var(--clr-sev-healthy)]' : 'border-transparent text-[var(--clr-text-muted)] hover:text-gray-700'}`}
            data-testid="tab-catalog"
          >
            <Settings className="w-4 h-4" />
            Pricing Catalog
          </button>
          <button
            onClick={() => setActiveTab('tenants')}
            className={`flex items-center gap-2 pb-3 px-1 text-sm font-medium border-b-2 transition-colors ${activeTab === 'tenants' ? 'border-green-600 text-[var(--clr-sev-healthy)]' : 'border-transparent text-[var(--clr-text-muted)] hover:text-gray-700'}`}
            data-testid="tab-tenants"
          >
            <Building2 className="w-4 h-4" />
            Tenants & Billing
          </button>
          <button
            onClick={() => setActiveTab('trials')}
            className={`flex items-center gap-2 pb-3 px-1 text-sm font-medium border-b-2 transition-colors ${activeTab === 'trials' ? 'border-green-600 text-[var(--clr-sev-healthy)]' : 'border-transparent text-[var(--clr-text-muted)] hover:text-gray-700'}`}
            data-testid="tab-trials"
          >
            <Gift className="w-4 h-4" />
            Free Trials
          </button>
          <button
            onClick={() => setActiveTab('invoices')}
            className={`flex items-center gap-2 pb-3 px-1 text-sm font-medium border-b-2 transition-colors ${activeTab === 'invoices' ? 'border-green-600 text-[var(--clr-sev-healthy)]' : 'border-transparent text-[var(--clr-text-muted)] hover:text-gray-700'}`}
            data-testid="tab-invoices"
          >
            <FileText className="w-4 h-4" />
            Invoices
          </button>
          <button
            onClick={() => setActiveTab('clients')}
            className={`flex items-center gap-2 pb-3 px-1 text-sm font-medium border-b-2 transition-colors ${activeTab === 'clients' ? 'border-green-600 text-[var(--clr-sev-healthy)]' : 'border-transparent text-[var(--clr-text-muted)] hover:text-gray-700'}`}
            data-testid="tab-clients"
          >
            <UserCircle className="w-4 h-4" />
            Clients
          </button>
        </div>
      </div>

      {activeTab === 'catalog' && (
        <CatalogTab
          currencies={currencies}
          selectedCurrency={selectedCurrency}
          setSelectedCurrency={setSelectedCurrency}
          currentCatalog={currentCatalog}
          setEditingTier={setEditingTier}
          formatCurrency={formatCurrency}
        />
      )}

      {activeTab === 'tenants' && (
        <TenantsTab
          tenants={tenants}
          tenantSearch={tenantSearch}
          setTenantSearch={setTenantSearch}
          loadTenants={loadTenants}
          loadTenantBilling={loadTenantBilling}
          selectedTenant={selectedTenant}
          tenantBilling={tenantBilling}
          tenantTrial={tenantTrial}
          setShowEditBilling={setShowEditBilling}
          setShowCreateTenant={setShowCreateTenant}
          setShowStartTrial={setShowStartTrial}
          handleEndTrial={handleEndTrial}
          formatCurrency={formatCurrency}
        />
      )}

      {activeTab === 'trials' && (
        <TrialsTab
          trials={trials}
          loadTrials={loadTrials}
          trialConfig={trialConfig}
          setTrialConfig={setTrialConfig}
        />
      )}

      {activeTab === 'invoices' && (
        <InvoicesTab
          invoices={invoices}
          loadInvoices={loadInvoices}
          setShowCreateInvoice={setShowCreateInvoice}
          handleUpdateInvoiceStatus={handleUpdateInvoiceStatus}
          formatCurrency={formatCurrency}
        />
      )}

      {activeTab === 'clients' && (
        <ClientsTab
          clients={clients}
          clientSearch={clientSearch}
          setClientSearch={setClientSearch}
          loadClients={loadClients}
          selectedClient={selectedClient}
          setSelectedClient={setSelectedClient}
          setShowEditClient={setShowEditClient}
          setShowCreateClient={setShowCreateClient}
          handleDeleteClient={handleDeleteClient}
          tenants={tenants}
          loadTenants={loadTenants}
        />
      )}

      {editingTier && (
        <EditPricingModal
          tier={editingTier}
          currency={selectedCurrency}
          onSave={handleSavePricing}
          onClose={() => setEditingTier(null)}
        />
      )}

      {showEditBilling && tenantBilling && (
        <EditBillingModal
          billing={tenantBilling.billing}
          currencies={currencies}
          onSave={handleUpdateBilling}
          onClose={() => setShowEditBilling(false)}
        />
      )}

      {showCreateTenant && (
        <CreateTenantModal
          currencies={currencies}
          onSave={handleCreateTenant}
          onClose={() => setShowCreateTenant(false)}
        />
      )}

      {showCreateInvoice && (
        <CreateInvoiceModal
          tenants={tenants}
          onSave={handleCreateInvoice}
          onClose={() => setShowCreateInvoice(false)}
        />
      )}

      {showStartTrial && selectedTenant && (
        <StartTrialModal
          tenant={selectedTenant}
          trialConfig={trialConfig}
          onStart={handleStartTrial}
          onClose={() => setShowStartTrial(false)}
        />
      )}

      {showCreateClient && (
        <ClientModal
          tenants={tenants}
          onSave={handleCreateClient}
          onClose={() => setShowCreateClient(false)}
        />
      )}

      {showEditClient && selectedClient && (
        <ClientModal
          client={selectedClient}
          tenants={tenants}
          onSave={handleUpdateClient}
          onClose={() => { setShowEditClient(false); setSelectedClient(null); }}
        />
      )}
    </div>
  );
};

const CatalogTab = ({ currencies, selectedCurrency, setSelectedCurrency, currentCatalog, setEditingTier, formatCurrency }) => {
  const renderFeatures = (features) => {
    if (!features) return <span className="text-sm text-[var(--clr-text-muted)]">No features defined</span>;
    if (typeof features === 'object' && !Array.isArray(features)) {
      const enabledFeatures = Object.entries(features).filter(([k, v]) => v === true).map(([k]) => k);
      if (enabledFeatures.length === 0) return <span className="text-sm text-[var(--clr-text-muted)]">No features enabled</span>;
      return enabledFeatures.slice(0, 3).map((f, i) => (
        <span key={i} className="px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded capitalize">{f.replace(/_/g, ' ')}</span>
      ));
    }
    if (Array.isArray(features)) {
      if (features.length === 0) return <span className="text-sm text-[var(--clr-text-muted)]">No features defined</span>;
      return features.slice(0, 3).map((f, i) => (
        <span key={i} className="px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded capitalize">{f}</span>
      ));
    }
    return <span className="text-sm text-[var(--clr-text-muted)]">No features defined</span>;
  };

  return (
    <div className="space-y-6">
      <div className="bg-[var(--clr-brand-primary)] rounded-lg p-4">
        <h3 className="font-medium text-white">Pricing Catalog Overview</h3>
        <p className="text-sm text-blue-100 mt-1">Manage pricing for all tiers across different currencies. ZAR is the default for South African tenants.</p>
      </div>

      <div className="flex items-center gap-4">
        <span className="text-sm font-medium text-[var(--clr-text-secondary)]">Select Currency:</span>
        <div className="flex gap-2">
          {currencies.map(curr => (
            <button
              key={curr}
              onClick={() => setSelectedCurrency(curr)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${selectedCurrency === curr ? 'bg-green-600 text-white' : 'bg-[var(--clr-surface-subtle)] text-gray-700 hover:bg-gray-200'}`}
              data-testid={`currency-btn-${curr}`}
            >
              {CURRENCY_SYMBOLS[curr]} {curr}
            </button>
          ))}
        </div>
      </div>

      <div className="bg-[var(--clr-surface-card)] rounded-lg border shadow-[var(--shadow-card)] overflow-hidden">
        <div className="p-4 border-b bg-[var(--clr-surface-subtle)]">
          <h3 className="font-semibold text-[var(--clr-text-primary)]">{selectedCurrency} Pricing Tiers</h3>
        </div>
        <table className="w-full">
          <thead className="bg-[var(--clr-surface-subtle)] border-b">
            <tr>
              <th className="text-left px-4 py-3 text-xs font-semibold text-[var(--clr-text-secondary)] uppercase">Tier</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-[var(--clr-text-secondary)] uppercase">Price/Month</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-[var(--clr-text-secondary)] uppercase">Key Features</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-[var(--clr-text-secondary)] uppercase">Limits</th>
              <th className="text-right px-4 py-3 text-xs font-semibold text-[var(--clr-text-secondary)] uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {currentCatalog.tiers && currentCatalog.tiers.length > 0 ? (
              currentCatalog.tiers.map(tier => (
                <tr key={tier.tier} className="hover:bg-[var(--clr-surface-hover)]" data-testid={`tier-row-${tier.tier}`}>
                  <td className="px-4 py-4">
                    <span className="font-medium text-[var(--clr-text-primary)] capitalize">{tier.tier}</span>
                    {tier.tier === 'enterprise' && (
                      <span className="ml-2 px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded">Custom</span>
                    )}
                  </td>
                  <td className="px-4 py-4">
                    <span className="text-lg font-bold text-[var(--clr-sev-healthy)]">{formatCurrency(tier.price, selectedCurrency)}</span>
                    {tier.price && <span className="text-xs text-[var(--clr-text-muted)] ml-1">/month</span>}
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex flex-wrap gap-1">
                      {renderFeatures(tier.features)}
                    </div>
                  </td>
                  <td className="px-4 py-4 text-sm text-[var(--clr-text-secondary)]">
                    {tier.limits && (
                      <div>
                        <div>{tier.limits.max_projects === -1 ? 'Unlimited' : tier.limits.max_projects} projects</div>
                        <div>{tier.limits.max_users === -1 ? 'Unlimited' : tier.limits.max_users} users</div>
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-4 text-right">
                    <button onClick={() => setEditingTier(tier)} className="p-2 hover:bg-[var(--clr-surface-hover)] rounded-lg" data-testid={`edit-tier-${tier.tier}`}>
                      <Edit className="w-4 h-4 text-[var(--clr-text-muted)]" />
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-[var(--clr-text-muted)]">
                  <AlertCircle className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                  No pricing data for {selectedCurrency}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const TenantsTab = ({ tenants, tenantSearch, setTenantSearch, loadTenants, loadTenantBilling, selectedTenant, tenantBilling, tenantTrial, setShowEditBilling, setShowCreateTenant, setShowStartTrial, handleEndTrial, formatCurrency }) => (
  <div className="space-y-6">
    <div className="flex items-center justify-between">
      <div className="bg-[var(--clr-sev-atrisk)] bg-opacity-10 border border-amber-200 rounded-lg p-4 flex-1 mr-4">
        <h3 className="font-medium text-amber-900">Tenant Management</h3>
        <p className="text-sm text-amber-700 mt-1">Create and manage organization tenants. Each tenant has their own billing settings, currency, and plan tier.</p>
      </div>
      <button
        onClick={() => setShowCreateTenant(true)}
        className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
        data-testid="create-tenant-btn"
      >
        <Plus className="w-4 h-4" />
        New Tenant
      </button>
    </div>

    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="bg-[var(--clr-surface-card)] rounded-lg border shadow-[var(--shadow-card)]">
        <div className="p-4 border-b">
          <h3 className="font-semibold text-[var(--clr-text-primary)] mb-3">Organizations</h3>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--clr-text-muted)]" />
            <input
              type="text"
              value={tenantSearch}
              onChange={e => setTenantSearch(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && loadTenants()}
              placeholder="Search tenants..."
              className="w-full pl-10 pr-4 py-2 border rounded-lg text-sm"
              data-testid="tenant-search"
            />
          </div>
        </div>
        <div className="divide-y max-h-96 overflow-y-auto">
          {tenants.length === 0 ? (
            <div className="p-4 text-center">
              <Building2 className="w-8 h-8 text-gray-300 mx-auto mb-2" />
              <p className="text-sm text-[var(--clr-text-muted)]">No tenants found</p>
            </div>
          ) : (
            tenants.map(tenant => (
              <button
                key={tenant.id}
                onClick={() => loadTenantBilling(tenant.id)}
                className={`w-full p-4 text-left hover:bg-[var(--clr-surface-hover)] ${selectedTenant && selectedTenant.id === tenant.id ? 'bg-[var(--clr-brand-primary)] bg-opacity-10 border-l-4 border-blue-500' : ''}`}
                data-testid={`tenant-${tenant.id}`}
              >
                <p className="font-medium text-[var(--clr-text-primary)]">{tenant.name}</p>
                <p className="text-xs text-[var(--clr-text-muted)] mt-0.5">{tenant.country}</p>
                {tenant.billing && (
                  <div className="flex items-center gap-2 mt-2">
                    <span className="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded font-medium">{tenant.billing.currency}</span>
                    <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded capitalize">{tenant.billing.tier}</span>
                  </div>
                )}
              </button>
            ))
          )}
        </div>
      </div>

      <div className="lg:col-span-2">
        {selectedTenant && tenantBilling ? (
          <div className="space-y-4">
            <div className="bg-[var(--clr-surface-card)] rounded-lg border shadow-[var(--shadow-card)] p-4">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold text-[var(--clr-text-primary)]">{selectedTenant.name}</h2>
                  <p className="text-sm text-[var(--clr-text-muted)]">{selectedTenant.country} • {selectedTenant.slug}</p>
                </div>
                <button onClick={() => setShowEditBilling(true)} className="px-4 py-2 bg-[var(--clr-brand-primary)] text-white rounded-lg text-sm hover:bg-[var(--clr-brand-primary-hover)]" data-testid="edit-billing-btn">
                  Edit Billing Settings
                </button>
              </div>
            </div>

              <div className="grid grid-cols-2 gap-4">
              <div className="bg-[var(--clr-surface-card)] rounded-lg border shadow-[var(--shadow-card)] p-4">
                <p className="text-sm text-[var(--clr-text-muted)] mb-1">Current Plan</p>
                <p className="text-2xl font-bold text-[var(--clr-text-primary)] capitalize">{tenantBilling.billing && tenantBilling.billing.tier}</p>
                <p className="text-xs text-[var(--clr-text-muted)] mt-1">
                  Since {tenantBilling.billing && tenantBilling.billing.effective_from ? new Date(tenantBilling.billing.effective_from).toLocaleDateString() : 'N/A'}
                </p>
              </div>
              <div className="bg-[var(--clr-surface-card)] rounded-lg border shadow-[var(--shadow-card)] p-4">
                <p className="text-sm text-[var(--clr-text-muted)] mb-1">Billing Currency</p>
                <p className="text-2xl font-bold text-[var(--clr-sev-healthy)]">{CURRENCY_SYMBOLS[tenantBilling.billing && tenantBilling.billing.currency]} {tenantBilling.billing && tenantBilling.billing.currency}</p>
                <p className="text-xs text-[var(--clr-text-muted)] mt-1">{tenantBilling.currency_config && tenantBilling.currency_config.name}</p>
              </div>
              <div className="bg-[var(--clr-surface-card)] rounded-lg border shadow-[var(--shadow-card)] p-4">
                <p className="text-sm text-[var(--clr-text-muted)] mb-1">Monthly Price</p>
                {tenantBilling.effective_pricing ? (
                  <p className="text-2xl font-bold text-[var(--clr-sev-healthy)]">{formatCurrency(tenantBilling.effective_pricing.price, tenantBilling.billing && tenantBilling.billing.currency)}</p>
                ) : (
                  <p className="text-2xl font-bold text-[var(--clr-text-muted)]">Custom</p>
                )}
                {tenantBilling.billing && tenantBilling.billing.custom_pricing && (
                  <span className="text-xs px-2 py-0.5 bg-purple-100 text-purple-700 rounded">Custom Pricing</span>
                )}
              </div>
              <div className="bg-[var(--clr-surface-card)] rounded-lg border shadow-[var(--shadow-card)] p-4">
                <p className="text-sm text-[var(--clr-text-muted)] mb-1">VAT Rate</p>
                <p className="text-2xl font-bold text-[var(--clr-text-primary)]">{tenantBilling.billing && tenantBilling.billing.vat_rate || 0}%</p>
                <p className="text-xs text-[var(--clr-text-muted)] mt-1">Renewal day: {tenantBilling.billing && tenantBilling.billing.renewal_day || 1}</p>
              </div>
            </div>

            {/* Trial Status Card */}
            {tenantTrial && (
              <div className={`rounded-lg border p-4 ${tenantTrial.has_trial && tenantTrial.trial?.status === 'active' ? 'bg-purple-50 border-purple-200' : 'bg-[var(--clr-surface-card)]'}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Gift className={`w-6 h-6 ${tenantTrial.has_trial && tenantTrial.trial?.status === 'active' ? 'text-purple-600' : 'text-gray-400'}`} />
                    <div>
                      <h3 className="font-semibold text-[var(--clr-text-primary)]">Free Trial Status</h3>
                      {tenantTrial.has_trial && tenantTrial.trial ? (
                        <div className="flex items-center gap-2 mt-1">
                          <span className={`text-xs px-2 py-0.5 rounded font-medium ${tenantTrial.trial.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}>
                            {tenantTrial.trial.status === 'active' ? 'ACTIVE' : 'EXPIRED'}
                          </span>
                          {tenantTrial.trial.status === 'active' && (
                            <span className="text-sm text-purple-700 font-medium">
                              {tenantTrial.trial.days_remaining} days remaining
                            </span>
                          )}
                        </div>
                      ) : (
                        <p className="text-sm text-[var(--clr-text-muted)]">No trial active</p>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {(!tenantTrial.has_trial || tenantTrial.trial?.status === 'expired') ? (
                      <button
                        onClick={() => setShowStartTrial(true)}
                        className="flex items-center gap-2 px-3 py-2 bg-purple-600 text-white rounded-lg text-sm hover:bg-purple-700"
                        data-testid="start-trial-btn"
                        disabled={tenantTrial.has_trial && tenantTrial.trial?.status === 'expired'}
                      >
                        <Play className="w-4 h-4" />
                        {tenantTrial.has_trial ? 'Trial Used' : 'Start Trial'}
                      </button>
                    ) : (
                      <button
                        onClick={() => handleEndTrial(selectedTenant.id)}
                        className="flex items-center gap-2 px-3 py-2 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700"
                        data-testid="end-trial-btn"
                      >
                        <Pause className="w-4 h-4" />
                        End Trial
                      </button>
                    )}
                  </div>
                </div>
                {tenantTrial.has_trial && tenantTrial.trial?.status === 'active' && (
                  <div className="mt-4 pt-4 border-t border-purple-200">
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div>
                        <p className="text-purple-600 font-medium">Trial Tier</p>
                        <p className="capitalize">{tenantTrial.trial.trial_tier}</p>
                      </div>
                      <div>
                        <p className="text-purple-600 font-medium">Started</p>
                        <p>{new Date(tenantTrial.trial.start_date).toLocaleDateString()}</p>
                      </div>
                      <div>
                        <p className="text-purple-600 font-medium">Ends</p>
                        <p>{new Date(tenantTrial.trial.end_date).toLocaleDateString()}</p>
                      </div>
                    </div>
                    <div className="mt-3 grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-purple-600 font-medium">Trial Limits</p>
                        <p>{tenantTrial.trial.limits?.max_projects || 50} projects, {tenantTrial.trial.limits?.max_users || 10} users</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Test Tenant Flag */}
            <div className="bg-[var(--clr-surface-card)] rounded-lg border shadow-[var(--shadow-card)] p-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-[var(--clr-text-primary)]">Test Tenant</h3>
                  <p className="text-sm text-[var(--clr-text-muted)]">
                    Test tenants are excluded from invoice generation
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={tenantBilling?.billing?.is_test_tenant || false}
                    onChange={async (e) => {
                      try {
                        await api.setTestTenantFlag(selectedTenant.id, e.target.checked);
                        loadTenantBilling(selectedTenant.id);
                      } catch (err) {
                        alert('Failed to update test flag');
                      }
                    }}
                    className="sr-only peer"
                    data-testid="test-tenant-toggle"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-amber-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-amber-500"></div>
                </label>
              </div>
              {tenantBilling?.billing?.is_test_tenant && (
                <div className="mt-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-xs text-amber-700">This tenant will not receive invoices</p>
                </div>
              )}
            </div>

            {tenantBilling.billing && tenantBilling.billing.billing_address && (
              <div className="bg-[var(--clr-surface-card)] rounded-lg border shadow-[var(--shadow-card)] p-4">
                <h3 className="font-semibold text-[var(--clr-text-primary)] mb-2">Billing Address</h3>
                <p className="text-sm text-[var(--clr-text-secondary)]">{tenantBilling.billing.billing_address.line1}</p>
                <p className="text-sm text-[var(--clr-text-secondary)]">{tenantBilling.billing.billing_address.city}, {tenantBilling.billing.billing_address.code}</p>
                <p className="text-sm text-[var(--clr-text-secondary)]">{tenantBilling.billing.billing_address.country}</p>
                <p className="text-sm text-[var(--clr-brand-primary)] mt-2">{tenantBilling.billing.billing_email}</p>
              </div>
            )}

            {tenantBilling.recent_events && tenantBilling.recent_events.length > 0 && (
              <div className="bg-[var(--clr-surface-card)] rounded-lg border shadow-[var(--shadow-card)]">
                <div className="p-4 border-b">
                  <h3 className="font-semibold text-[var(--clr-text-primary)]">Recent Subscription Events</h3>
                </div>
                <div className="divide-y max-h-48 overflow-y-auto">
                  {tenantBilling.recent_events.map((event, idx) => (
                    <div key={idx} className="p-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-[var(--clr-text-primary)]">{event.event_type}</span>
                        <span className="text-xs text-[var(--clr-text-muted)]">{new Date(event.created_at).toLocaleString()}</span>
                      </div>
                      {event.from_tier && event.to_tier && (
                        <p className="text-xs text-[var(--clr-text-secondary)] mt-1">{event.from_tier} → {event.to_tier}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="bg-[var(--clr-surface-card)] rounded-lg border shadow-[var(--shadow-card)] p-8 text-center h-full flex flex-col items-center justify-center">
            <Building2 className="w-16 h-16 text-gray-300 mb-4" />
            <p className="text-[var(--clr-text-secondary)] font-medium">Select a tenant to view billing details</p>
            <p className="text-sm text-[var(--clr-text-muted)] mt-1">Or create a new tenant to get started</p>
          </div>
        )}
      </div>
    </div>
  </div>
);

const InvoicesTab = ({ invoices, loadInvoices, setShowCreateInvoice, handleUpdateInvoiceStatus, formatCurrency }) => {
  const handleDownloadPdf = async (invoiceId, invoiceNumber) => {
    try {
      const response = await api.downloadInvoicePdf(invoiceId);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${invoiceNumber || 'invoice'}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download PDF:', error);
      alert('Failed to download PDF');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <button onClick={loadInvoices} className="p-2 hover:bg-[var(--clr-surface-hover)] rounded-lg" data-testid="refresh-invoices">
          <RefreshCw className="w-5 h-5 text-[var(--clr-text-secondary)]" />
        </button>
        <div className="flex gap-2">
          <a 
            href="/billing/invoice/new" 
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            data-testid="create-project-invoice-btn"
          >
            <FileText className="w-4 h-4" />
            Project Invoice
          </a>
          <button onClick={() => setShowCreateInvoice(true)} className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700" data-testid="create-invoice-btn">
            <Plus className="w-4 h-4" />
            Quick Invoice
          </button>
        </div>
      </div>

      <div className="bg-[var(--clr-surface-card)] rounded-lg border shadow-[var(--shadow-card)] overflow-hidden">
        <table className="w-full">
          <thead className="bg-[var(--clr-surface-subtle)] border-b">
            <tr>
              <th className="text-left px-4 py-3 text-xs font-semibold text-[var(--clr-text-secondary)]">Invoice #</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-[var(--clr-text-secondary)]">Tenant</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-[var(--clr-text-secondary)]">Project</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-[var(--clr-text-secondary)]">Date</th>
              <th className="text-right px-4 py-3 text-xs font-semibold text-[var(--clr-text-secondary)]">Amount</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-[var(--clr-text-secondary)]">Status</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-[var(--clr-text-secondary)]">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {invoices.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-[var(--clr-text-muted)]">
                  <FileText className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                  No invoices yet
                </td>
              </tr>
            ) : (
              invoices.map(invoice => (
                <tr key={invoice.id} className="hover:bg-[var(--clr-surface-hover)]" data-testid={`invoice-${invoice.id}`}>
                  <td className="px-4 py-3 text-sm font-medium text-[var(--clr-text-primary)]">
                    <a href={`/billing/invoice/${invoice.id}`} className="text-blue-600 hover:underline">
                      {invoice.invoice_number}
                    </a>
                  </td>
                  <td className="px-4 py-3 text-sm text-[var(--clr-text-secondary)]">{invoice.tenant_name}</td>
                  <td className="px-4 py-3 text-sm text-[var(--clr-text-secondary)]">{invoice.project_name || '-'}</td>
                  <td className="px-4 py-3 text-sm text-[var(--clr-text-secondary)]">{new Date(invoice.invoice_date).toLocaleDateString()}</td>
                  <td className="px-4 py-3 text-sm text-right font-medium text-[var(--clr-text-primary)]">{formatCurrency(invoice.total, invoice.currency)}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${invoice.status === 'paid' ? 'bg-green-100 text-green-700' : invoice.status === 'issued' ? 'bg-blue-100 text-blue-700' : 'bg-[var(--clr-surface-subtle)] text-gray-700'}`}>
                      {invoice.status}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <button 
                        onClick={() => handleDownloadPdf(invoice.id, invoice.invoice_number)} 
                        className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"
                        title="Download PDF"
                        data-testid={`download-pdf-${invoice.id}`}
                      >
                        <Download className="w-4 h-4" />
                      </button>
                      {invoice.status === 'issued' && (
                        <button onClick={() => handleUpdateInvoiceStatus(invoice.id, 'paid')} className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded hover:bg-green-200" data-testid={`mark-paid-${invoice.id}`}>
                          Mark Paid
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const EditPricingModal = ({ tier, currency, onSave, onClose }) => {
  const [price, setPrice] = useState(tier.price || 0);
  
  // Feature groups with proper labels
  const FEATURE_GROUPS = {
    views: {
      label: 'Views & Navigation',
      features: [
        { key: 'portfolio_view', label: 'Portfolio View' },
        { key: 'program_view', label: 'Program View' },
        { key: 'project_view', label: 'Project View' },
        { key: 'epic_view', label: 'Epic View' },
        { key: 'feature_view', label: 'Feature View' },
        { key: 'story_view', label: 'Story View' },
        { key: 'task_view', label: 'Task View' },
        { key: 'roadmap_view', label: 'Roadmap View' }
      ]
    },
    dashboards: {
      label: 'Dashboards',
      features: [
        { key: 'exec_dash', label: 'Executive Dashboard' },
        { key: 'pmo_dash', label: 'PMO Dashboard' },
        { key: 'pm_dash', label: 'Project Manager Dashboard' }
      ]
    },
    raid: {
      label: 'Governance / RAID',
      features: [
        { key: 'risk_view', label: 'Risks' },
        { key: 'issue_tracking', label: 'Issues' },
        { key: 'dependency_tracking', label: 'Dependencies' },
        { key: 'decision_tracking', label: 'Decisions' }
      ]
    },
    editing: {
      label: 'Editing Capabilities',
      features: [
        { key: 'manual_editing', label: 'Manual Editing', options: ['none', 'limited', 'full'] },
        { key: 'roadmap_editing', label: 'Roadmap Editing', options: ['none', 'limited', 'full'] }
      ]
    },
    signals: {
      label: 'Automation & Signals',
      features: [
        { key: 'daily_signal', label: 'Daily Signal™', options: ['none', 'email', 'whatsapp_email', 'custom'] }
      ]
    },
    pmo_tools: {
      label: 'PMO Tools',
      features: [
        { key: 'steerco_pack', label: 'Steerco Pack Generator' },
        { key: 'team_management', label: 'Team Utilization' },
        { key: 'versioning', label: 'Versioning' },
        { key: 'audit_logs', label: 'Audit Logs' },
        { key: 'sprint_planning', label: 'Sprint Planning' },
        { key: 'reports', label: 'Reports' },
        { key: 'export_data', label: 'Export Data' }
      ]
    },
    integrations: {
      label: 'Integrations',
      features: [
        { key: 'integrations', label: 'Azure DevOps / Jira', options: ['none', 'addon', 'included'] },
        { key: 'bulk_upload', label: 'Bulk Upload (Excel)' }
      ]
    },
    security: {
      label: 'Security & Administration',
      features: [
        { key: 'sso_integration', label: 'SSO / SCIM' },
        { key: 'api_access', label: 'API Access' },
        { key: 'compliance_mode', label: 'Compliance Mode' },
        { key: 'custom_branding', label: 'Custom Branding' }
      ]
    }
  };
  
  // Initialize features state
  const initFeatures = () => {
    if (tier.features && typeof tier.features === 'object' && !Array.isArray(tier.features)) {
      return { ...tier.features };
    }
    return {};
  };
  
  const [features, setFeatures] = useState(initFeatures);
  const [limits, setLimits] = useState({
    max_projects: tier.limits?.max_projects ?? 10,
    max_users: tier.limits?.max_users ?? 5
  });
  const [expandedGroups, setExpandedGroups] = useState(['views', 'raid', 'editing', 'signals']);

  const toggleFeature = (key, value) => {
    setFeatures(prev => ({ ...prev, [key]: value }));
  };

  const toggleGroup = (groupKey) => {
    setExpandedGroups(prev => 
      prev.includes(groupKey) 
        ? prev.filter(g => g !== groupKey)
        : [...prev, groupKey]
    );
  };

  const enabledCount = Object.entries(features).filter(([k, v]) => v && v !== false && v !== 'none').length;

  const renderFeatureControl = (feature) => {
    if (feature.options) {
      // Dropdown for options
      return (
        <select
          value={features[feature.key] || feature.options[0]}
          onChange={e => toggleFeature(feature.key, e.target.value)}
          className="w-32 px-2 py-1 text-xs border rounded bg-white"
        >
          {feature.options.map(opt => (
            <option key={opt} value={opt}>
              {opt === 'none' ? 'Disabled' : 
               opt === 'limited' ? 'Limited' : 
               opt === 'full' ? 'Full' : 
               opt === 'email' ? 'Email Only' :
               opt === 'whatsapp_email' ? 'WhatsApp + Email' :
               opt === 'custom' ? 'Custom Templates' :
               opt === 'addon' ? 'Add-on' :
               opt === 'included' ? 'Included' : opt}
            </option>
          ))}
        </select>
      );
    }
    // Checkbox for boolean
    return (
      <input
        type="checkbox"
        checked={features[feature.key] === true}
        onChange={e => toggleFeature(feature.key, e.target.checked)}
        className="w-4 h-4 text-green-600 rounded border-gray-300 focus:ring-green-500"
      />
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/50" onClick={onClose}></div>
      <div className="relative bg-[var(--clr-surface-card)] rounded-xl shadow-xl w-full max-w-3xl p-6 z-10 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-[var(--clr-text-primary)]">Edit {tier.tier} Pricing ({currency})</h2>
          <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">{enabledCount} features enabled</span>
        </div>
        
        {/* Price & Limits Row */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Monthly Price</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--clr-text-muted)]">{CURRENCY_SYMBOLS[currency]}</span>
              <input type="number" value={price} onChange={e => setPrice(parseFloat(e.target.value) || 0)} className="w-full pl-8 pr-4 py-2 border rounded-lg" data-testid="price-input" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Max Projects</label>
            <input 
              type="number" 
              value={limits.max_projects} 
              onChange={e => setLimits(prev => ({ ...prev, max_projects: parseInt(e.target.value) || 0 }))} 
              className="w-full px-3 py-2 border rounded-lg text-sm" 
              placeholder="-1 = unlimited"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Max Users</label>
            <input 
              type="number" 
              value={limits.max_users} 
              onChange={e => setLimits(prev => ({ ...prev, max_users: parseInt(e.target.value) || 0 }))} 
              className="w-full px-3 py-2 border rounded-lg text-sm" 
              placeholder="-1 = unlimited"
            />
          </div>
        </div>

        {/* Grouped Features */}
        <div className="space-y-3 mb-6">
          <label className="block text-sm font-medium text-gray-700">Feature Configuration</label>
          
          {Object.entries(FEATURE_GROUPS).map(([groupKey, group]) => (
            <div key={groupKey} className="border rounded-lg overflow-hidden">
              <button
                onClick={() => toggleGroup(groupKey)}
                className="w-full flex items-center justify-between px-4 py-2 bg-[var(--clr-surface-subtle)] hover:bg-gray-100 transition-colors"
              >
                <span className="font-medium text-sm text-[var(--clr-text-primary)]">{group.label}</span>
                <span className="text-xs text-[var(--clr-text-muted)]">
                  {group.features.filter(f => features[f.key] && features[f.key] !== false && features[f.key] !== 'none').length}/{group.features.length} enabled
                </span>
              </button>
              {expandedGroups.includes(groupKey) && (
                <div className="p-3 bg-white grid grid-cols-2 gap-2">
                  {group.features.map(feature => (
                    <div key={feature.key} className="flex items-center justify-between p-2 rounded hover:bg-gray-50">
                      <span className="text-sm text-gray-700">{feature.label}</span>
                      {renderFeatureControl(feature)}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t">
          <button onClick={onClose} className="px-4 py-2 border rounded-lg hover:bg-[var(--clr-surface-hover)]">Cancel</button>
          <button onClick={() => onSave({ tier: tier.tier, price, features, limits })} className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700" data-testid="save-price-btn">
            <Check className="w-4 h-4 inline mr-1" />Save Changes
          </button>
        </div>
      </div>
    </div>
  );
};

const EditBillingModal = ({ billing, currencies, onSave, onClose }) => {
  const [formData, setFormData] = useState({
    tier: billing ? billing.tier : 'starter',
    currency: billing ? billing.currency : 'ZAR',
    vat_rate: billing ? billing.vat_rate : 15
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/50" onClick={onClose}></div>
      <div className="relative bg-[var(--clr-surface-card)] rounded-xl shadow-xl w-full max-w-md p-6 z-10">
        <h2 className="text-xl font-bold text-[var(--clr-text-primary)] mb-4">Edit Billing Settings</h2>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Plan Tier</label>
            <select value={formData.tier} onChange={e => setFormData(prev => ({ ...prev, tier: e.target.value }))} className="w-full px-3 py-2 border rounded-lg" data-testid="tier-select">
              <option value="starter">Starter</option>
              <option value="growth">Growth</option>
              <option value="business">Business</option>
              <option value="enterprise">Enterprise</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Currency</label>
            <select value={formData.currency} onChange={e => setFormData(prev => ({ ...prev, currency: e.target.value }))} className="w-full px-3 py-2 border rounded-lg" data-testid="currency-select">
              {currencies.map(c => (
                <option key={c} value={c}>{CURRENCY_SYMBOLS[c]} {c}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">VAT Rate (%)</label>
            <input type="number" value={formData.vat_rate} onChange={e => setFormData(prev => ({ ...prev, vat_rate: parseFloat(e.target.value) || 0 }))} className="w-full px-3 py-2 border rounded-lg" data-testid="vat-input" />
          </div>
        </div>
        <div className="mt-6 flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 border rounded-lg hover:bg-[var(--clr-surface-hover)]">Cancel</button>
          <button onClick={() => onSave(formData)} className="px-4 py-2 bg-[var(--clr-brand-primary)] text-white rounded-lg hover:bg-[var(--clr-brand-primary-hover)]" data-testid="save-billing-btn">Save Changes</button>
        </div>
      </div>
    </div>
  );
};

const CreateTenantModal = ({ currencies, onSave, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    country: 'ZA',
    currency: 'ZAR',
    tier: 'starter',
    billing_email: ''
  });

  const handleNameChange = (name) => {
    setFormData(prev => ({
      ...prev,
      name,
      slug: name.toLowerCase().replace(/[^a-z0-9]/g, '-').replace(/-+/g, '-')
    }));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/50" onClick={onClose}></div>
      <div className="relative bg-[var(--clr-surface-card)] rounded-xl shadow-xl w-full max-w-lg p-6 z-10">
        <h2 className="text-xl font-bold text-[var(--clr-text-primary)] mb-4">Create New Tenant</h2>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Organization Name *</label>
            <input
              type="text"
              value={formData.name}
              onChange={e => handleNameChange(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg"
              placeholder="Acme Corporation"
              data-testid="tenant-name-input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Slug</label>
            <input
              type="text"
              value={formData.slug}
              onChange={e => setFormData(prev => ({ ...prev, slug: e.target.value }))}
              className="w-full px-3 py-2 border rounded-lg text-[var(--clr-text-muted)]"
              placeholder="acme-corporation"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
              <select value={formData.country} onChange={e => setFormData(prev => ({ ...prev, country: e.target.value }))} className="w-full px-3 py-2 border rounded-lg">
                <option value="ZA">South Africa</option>
                <option value="US">United States</option>
                <option value="GB">United Kingdom</option>
                <option value="DE">Germany</option>
                <option value="AU">Australia</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Billing Currency</label>
              <select value={formData.currency} onChange={e => setFormData(prev => ({ ...prev, currency: e.target.value }))} className="w-full px-3 py-2 border rounded-lg" data-testid="tenant-currency-select">
                {currencies.map(c => (
                  <option key={c} value={c}>{CURRENCY_SYMBOLS[c]} {c}</option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Starting Plan</label>
            <select value={formData.tier} onChange={e => setFormData(prev => ({ ...prev, tier: e.target.value }))} className="w-full px-3 py-2 border rounded-lg" data-testid="tenant-tier-select">
              <option value="starter">Starter</option>
              <option value="growth">Growth</option>
              <option value="business">Business</option>
              <option value="enterprise">Enterprise</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Billing Email *</label>
            <input
              type="email"
              value={formData.billing_email}
              onChange={e => setFormData(prev => ({ ...prev, billing_email: e.target.value }))}
              className="w-full px-3 py-2 border rounded-lg"
              placeholder="billing@company.com"
              data-testid="tenant-email-input"
            />
          </div>
        </div>
        <div className="mt-6 flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 border rounded-lg hover:bg-[var(--clr-surface-hover)]">Cancel</button>
          <button
            onClick={() => formData.name && formData.billing_email && onSave(formData)}
            disabled={!formData.name || !formData.billing_email}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
            data-testid="create-tenant-submit"
          >
            Create Tenant
          </button>
        </div>
      </div>
    </div>
  );
};

const CreateInvoiceModal = ({ tenants, onSave, onClose }) => {
  const [selectedTenantId, setSelectedTenantId] = useState('');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/50" onClick={onClose}></div>
      <div className="relative bg-[var(--clr-surface-card)] rounded-xl shadow-xl w-full max-w-md p-6 z-10">
        <h2 className="text-xl font-bold text-[var(--clr-text-primary)] mb-4">Create Invoice</h2>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Select Tenant</label>
          <select value={selectedTenantId} onChange={e => setSelectedTenantId(e.target.value)} className="w-full px-3 py-2 border rounded-lg" data-testid="invoice-tenant-select">
            <option value="">Choose tenant...</option>
            {tenants.map(t => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
        </div>
        <p className="text-sm text-[var(--clr-text-muted)] mt-4">Invoice will be generated based on the tenant's current plan and pricing.</p>
        <div className="mt-6 flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 border rounded-lg hover:bg-[var(--clr-surface-hover)]">Cancel</button>
          <button
            onClick={() => selectedTenantId && onSave(selectedTenantId)}
            disabled={!selectedTenantId}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
            data-testid="create-invoice-submit"
          >
            Create Invoice
          </button>
        </div>
      </div>
    </div>
  );
};


// ===========================
// Trials Tab Component
// ===========================

const TrialsTab = ({ trials, loadTrials, trialConfig, setTrialConfig }) => {
  const [configForm, setConfigForm] = useState({
    default_duration_days: trialConfig?.default_duration_days || 30,
    max_projects: trialConfig?.trial_limits?.max_projects || 50,
    max_users: trialConfig?.trial_limits?.max_users || 10,
    post_trial_action: trialConfig?.post_trial_action || 'downgrade_to_starter',
    allow_whatsapp: trialConfig?.allow_whatsapp !== false, // Default true
  });

  const handleSaveConfig = async () => {
    try {
      await api.updateTrialConfig({
        default_duration_days: configForm.default_duration_days,
        trial_limits: {
          max_projects: configForm.max_projects,
          max_users: configForm.max_users
        },
        post_trial_action: configForm.post_trial_action,
        allow_whatsapp: configForm.allow_whatsapp
      });
      alert('Trial configuration updated!');
    } catch (error) {
      alert('Failed to update config: ' + error.message);
    }
  };

  return (
    <div className="space-y-6">
      {/* Trial Configuration Card */}
      <div className="bg-purple-50 border border-purple-200 rounded-xl p-6">
        <div className="flex items-center gap-3 mb-4">
          <Gift className="w-6 h-6 text-purple-600" />
          <div>
            <h3 className="font-semibold text-purple-900">Business Trial Mode Configuration</h3>
            <p className="text-sm text-purple-700">Configure the free trial settings for new tenants</p>
          </div>
        </div>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
          <div>
            <label className="block text-sm font-medium text-purple-800 mb-1">Default Duration</label>
            <select
              value={configForm.default_duration_days}
              onChange={e => setConfigForm(prev => ({ ...prev, default_duration_days: parseInt(e.target.value) }))}
              className="w-full px-3 py-2 border border-purple-200 rounded-lg bg-white text-sm"
            >
              <option value={30}>30 Days</option>
              <option value={60}>60 Days</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-purple-800 mb-1">Max Projects</label>
            <input
              type="number"
              value={configForm.max_projects}
              onChange={e => setConfigForm(prev => ({ ...prev, max_projects: parseInt(e.target.value) || 10 }))}
              className="w-full px-3 py-2 border border-purple-200 rounded-lg bg-white text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-purple-800 mb-1">Max Users</label>
            <input
              type="number"
              value={configForm.max_users}
              onChange={e => setConfigForm(prev => ({ ...prev, max_users: parseInt(e.target.value) || 5 }))}
              className="w-full px-3 py-2 border border-purple-200 rounded-lg bg-white text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-purple-800 mb-1">After Trial Ends</label>
            <select
              value={configForm.post_trial_action}
              onChange={e => setConfigForm(prev => ({ ...prev, post_trial_action: e.target.value }))}
              className="w-full px-3 py-2 border border-purple-200 rounded-lg bg-white text-sm"
            >
              <option value="downgrade_to_starter">Downgrade to Starter</option>
              <option value="lock_editing">Lock Editing Only</option>
            </select>
          </div>
        </div>

        {/* Trial Feature Switches */}
        <div className="mt-6 border-t border-purple-200 pt-4">
          <h4 className="font-medium text-purple-900 mb-3">Trial Feature Controls</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* WhatsApp Toggle */}
            <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-purple-100">
              <div>
                <p className="font-medium text-gray-800">Allow WhatsApp During Trial</p>
                <p className="text-xs text-gray-500">Enable Daily Signal™ WhatsApp notifications</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={configForm.allow_whatsapp}
                  onChange={e => setConfigForm(prev => ({ ...prev, allow_whatsapp: e.target.checked }))}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
              </label>
            </div>

            {/* Locked Features - Always Disabled */}
            <div className="flex items-center justify-between p-3 bg-gray-100 rounded-lg border border-gray-200">
              <div>
                <p className="font-medium text-gray-600">API Access</p>
                <p className="text-xs text-gray-400">Disabled during trial</p>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-red-600 bg-red-100 px-2 py-1 rounded">LOCKED OFF</span>
              </div>
            </div>

            <div className="flex items-center justify-between p-3 bg-gray-100 rounded-lg border border-gray-200">
              <div>
                <p className="font-medium text-gray-600">SSO / SCIM</p>
                <p className="text-xs text-gray-400">Disabled during trial</p>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-red-600 bg-red-100 px-2 py-1 rounded">LOCKED OFF</span>
              </div>
            </div>

            <div className="flex items-center justify-between p-3 bg-gray-100 rounded-lg border border-gray-200">
              <div>
                <p className="font-medium text-gray-600">Integrations (ADO/Jira)</p>
                <p className="text-xs text-gray-400">Disabled during trial</p>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-red-600 bg-red-100 px-2 py-1 rounded">LOCKED OFF</span>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-4 p-3 bg-purple-100 rounded-lg">
          <p className="text-sm text-purple-800">
            <strong>Trial Features:</strong> Business Trial Mode provides all Business tier features except API Access, SSO, and Integrations.
            {configForm.allow_whatsapp ? ' Daily Signal™ with WhatsApp is enabled during trial.' : ' Daily Signal™ is limited to Email only during trial.'}
          </p>
        </div>

        <div className="mt-4 flex justify-end">
          <button
            onClick={handleSaveConfig}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm"
          >
            Save Configuration
          </button>
        </div>
      </div>

      {/* Active Trials List */}
      <div className="bg-[var(--clr-surface-card)] rounded-xl border shadow-[var(--shadow-card)]">
        <div className="p-4 border-b flex items-center justify-between">
          <h3 className="font-semibold text-[var(--clr-text-primary)]">Active & Recent Trials</h3>
          <button
            onClick={loadTrials}
            className="p-2 hover:bg-[var(--clr-surface-hover)] rounded-lg"
          >
            <RefreshCw className="w-4 h-4 text-[var(--clr-text-muted)]" />
          </button>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[var(--clr-surface-subtle)] text-left">
              <tr>
                <th className="p-3 font-medium">Tenant</th>
                <th className="p-3 font-medium">Trial Tier</th>
                <th className="p-3 font-medium">Started</th>
                <th className="p-3 font-medium">Ends</th>
                <th className="p-3 font-medium">Days Left</th>
                <th className="p-3 font-medium">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {trials.length === 0 ? (
                <tr>
                  <td colSpan="6" className="p-8 text-center text-[var(--clr-text-muted)]">
                    <Gift className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                    No trials found
                  </td>
                </tr>
              ) : (
                trials.map((trial, idx) => (
                  <tr key={idx} className="hover:bg-[var(--clr-surface-hover)]">
                    <td className="p-3 font-medium">{trial.tenant_name}</td>
                    <td className="p-3 capitalize">{trial.trial_tier}</td>
                    <td className="p-3">{new Date(trial.start_date).toLocaleDateString()}</td>
                    <td className="p-3">{new Date(trial.end_date).toLocaleDateString()}</td>
                    <td className="p-3">
                      {trial.status === 'active' ? (
                        <span className={`font-medium ${trial.days_remaining <= 5 ? 'text-red-600' : trial.days_remaining <= 15 ? 'text-amber-600' : 'text-green-600'}`}>
                          {trial.days_remaining} days
                        </span>
                      ) : '-'}
                    </td>
                    <td className="p-3">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${trial.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}>
                        {trial.status.toUpperCase()}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};


// ===========================
// Start Trial Modal
// ===========================

const StartTrialModal = ({ tenant, trialConfig, onStart, onClose }) => {
  const [duration, setDuration] = useState(trialConfig?.default_duration_days || 30);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/50" onClick={onClose}></div>
      <div className="relative bg-[var(--clr-surface-card)] rounded-xl shadow-xl w-full max-w-md p-6 z-10">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
            <Gift className="w-6 h-6 text-purple-600" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-[var(--clr-text-primary)]">Start Free Trial</h2>
            <p className="text-sm text-[var(--clr-text-muted)]">{tenant.name}</p>
          </div>
        </div>

        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-4">
          <h4 className="font-medium text-purple-900 mb-2">Business Trial Mode</h4>
          <ul className="text-sm text-purple-700 space-y-1">
            <li>• All Business tier features enabled</li>
            <li>• Daily Signal™ with WhatsApp + Email</li>
            <li>• {trialConfig?.trial_limits?.max_projects || 50} projects, {trialConfig?.trial_limits?.max_users || 10} users limit</li>
            <li>• No API Access or Integrations</li>
          </ul>
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">Trial Duration</label>
          <div className="flex gap-3">
            <button
              onClick={() => setDuration(30)}
              className={`flex-1 py-3 rounded-lg border-2 transition-colors ${duration === 30 ? 'border-purple-500 bg-purple-50 text-purple-700' : 'border-gray-200 hover:border-gray-300'}`}
            >
              <span className="text-xl font-bold">30</span>
              <span className="text-sm block">Days</span>
            </button>
            <button
              onClick={() => setDuration(60)}
              className={`flex-1 py-3 rounded-lg border-2 transition-colors ${duration === 60 ? 'border-purple-500 bg-purple-50 text-purple-700' : 'border-gray-200 hover:border-gray-300'}`}
            >
              <span className="text-xl font-bold">60</span>
              <span className="text-sm block">Days</span>
            </button>
          </div>
        </div>

        <p className="text-xs text-[var(--clr-text-muted)] mb-4">
          After trial ends, tenant will be automatically downgraded to Starter tier.
        </p>

        <div className="flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 border rounded-lg hover:bg-[var(--clr-surface-hover)]">
            Cancel
          </button>
          <button
            onClick={() => onStart(tenant.id, duration)}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
            data-testid="confirm-start-trial"
          >
            <Play className="w-4 h-4 inline mr-2" />
            Start {duration}-Day Trial
          </button>
        </div>
      </div>
    </div>
  );
};

// ===========================
// Clients Tab
// ===========================

const ClientsTab = ({ clients, clientSearch, setClientSearch, loadClients, selectedClient, setSelectedClient, setShowEditClient, setShowCreateClient, handleDeleteClient, tenants, loadTenants }) => {
  useEffect(() => {
    // Load all clients on mount (no search filter)
    loadClients('');
    if (tenants.length === 0) loadTenants();
  }, []);

  const handleSearch = () => {
    loadClients(clientSearch);
  };

  const getTenantName = (tenantId) => {
    const tenant = tenants.find(t => t.id === tenantId);
    return tenant ? tenant.name : 'Unknown';
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex-1 mr-4">
          <h3 className="font-medium text-blue-900">Client Billing Profiles</h3>
          <p className="text-sm text-blue-700 mt-1">Manage client details for invoice generation. Each client has billing address, VAT number, and payment terms.</p>
        </div>
        <button
          onClick={() => setShowCreateClient(true)}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          data-testid="create-client-btn"
        >
          <Plus className="w-4 h-4" />
          New Client
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Client List */}
        <div className="bg-[var(--clr-surface-card)] rounded-lg border shadow-[var(--shadow-card)]">
          <div className="p-4 border-b">
            <h3 className="font-semibold text-[var(--clr-text-primary)] mb-3">Client Profiles</h3>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--clr-text-muted)]" />
              <input
                type="text"
                value={clientSearch}
                onChange={e => setClientSearch(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleSearch()}
                placeholder="Search clients..."
                className="w-full pl-10 pr-4 py-2 border rounded-lg text-sm"
                data-testid="client-search"
              />
            </div>
          </div>
          <div className="divide-y max-h-[500px] overflow-y-auto">
            {clients.length === 0 ? (
              <div className="p-4 text-center">
                <UserCircle className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                <p className="text-sm text-[var(--clr-text-muted)]">No clients found</p>
                <button
                  onClick={() => setShowCreateClient(true)}
                  className="mt-2 text-sm text-blue-600 hover:underline"
                >
                  Create your first client
                </button>
              </div>
            ) : (
              clients.map(client => (
                <button
                  key={client.id}
                  onClick={() => setSelectedClient(client)}
                  className={`w-full p-4 text-left hover:bg-[var(--clr-surface-hover)] ${selectedClient && selectedClient.id === client.id ? 'bg-blue-50 border-l-4 border-blue-500' : ''}`}
                  data-testid={`client-${client.id}`}
                >
                  <p className="font-medium text-[var(--clr-text-primary)]">{client.legal_name}</p>
                  <p className="text-xs text-[var(--clr-text-muted)] mt-0.5">{getTenantName(client.tenant_id)}</p>
                  {client.vat_number && (
                    <span className="inline-block mt-1 text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded">
                      VAT: {client.vat_number}
                    </span>
                  )}
                </button>
              ))
            )}
          </div>
        </div>

        {/* Client Details */}
        <div className="lg:col-span-2">
          {selectedClient ? (
            <div className="space-y-4">
              <div className="bg-[var(--clr-surface-card)] rounded-lg border shadow-[var(--shadow-card)] p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-xl font-bold text-[var(--clr-text-primary)]">{selectedClient.legal_name}</h2>
                    <p className="text-sm text-[var(--clr-text-muted)]">
                      {getTenantName(selectedClient.tenant_id)}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setShowEditClient(true)}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700"
                      data-testid="edit-client-btn"
                    >
                      <Edit className="w-4 h-4 inline mr-1" />
                      Edit
                    </button>
                    <button
                      onClick={() => handleDeleteClient(selectedClient.id)}
                      className="px-3 py-2 text-red-600 border border-red-200 rounded-lg text-sm hover:bg-red-50"
                      data-testid="delete-client-btn"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="bg-[var(--clr-surface-card)] rounded-lg border shadow-[var(--shadow-card)] p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <MapPin className="w-4 h-4 text-blue-600" />
                    <p className="text-sm text-[var(--clr-text-muted)]">Billing Address</p>
                  </div>
                  {selectedClient.billing_address ? (
                    <div className="text-sm text-[var(--clr-text-primary)]">
                      {selectedClient.billing_address.line1 && <p>{selectedClient.billing_address.line1}</p>}
                      {selectedClient.billing_address.line2 && <p>{selectedClient.billing_address.line2}</p>}
                      {(selectedClient.billing_address.city || selectedClient.billing_address.postal_code) && (
                        <p>{selectedClient.billing_address.city} {selectedClient.billing_address.postal_code}</p>
                      )}
                      {selectedClient.billing_address.province && <p>{selectedClient.billing_address.province}</p>}
                      {selectedClient.billing_address.country && <p>{selectedClient.billing_address.country}</p>}
                    </div>
                  ) : (
                    <p className="text-sm text-[var(--clr-text-muted)] italic">No address provided</p>
                  )}
                </div>

                <div className="bg-[var(--clr-surface-card)] rounded-lg border shadow-[var(--shadow-card)] p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <FileText className="w-4 h-4 text-green-600" />
                    <p className="text-sm text-[var(--clr-text-muted)]">Registration Details</p>
                  </div>
                  <div className="space-y-2">
                    <div>
                      <p className="text-xs text-[var(--clr-text-muted)]">VAT Number</p>
                      <p className="text-sm font-medium text-[var(--clr-text-primary)]">{selectedClient.vat_number || '-'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-[var(--clr-text-muted)]">Registration No.</p>
                      <p className="text-sm font-medium text-[var(--clr-text-primary)]">{selectedClient.registration_number || '-'}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-[var(--clr-surface-card)] rounded-lg border shadow-[var(--shadow-card)] p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Mail className="w-4 h-4 text-purple-600" />
                    <p className="text-sm text-[var(--clr-text-muted)]">Contact</p>
                  </div>
                  <div className="space-y-2">
                    <div>
                      <p className="text-xs text-[var(--clr-text-muted)]">Contact Person</p>
                      <p className="text-sm font-medium text-[var(--clr-text-primary)]">{selectedClient.contact_person || '-'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-[var(--clr-text-muted)]">Email</p>
                      <p className="text-sm font-medium text-[var(--clr-text-primary)]">{selectedClient.contact_email || '-'}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-[var(--clr-surface-card)] rounded-lg border shadow-[var(--shadow-card)] p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Clock className="w-4 h-4 text-amber-600" />
                    <p className="text-sm text-[var(--clr-text-muted)]">Payment Terms</p>
                  </div>
                  <p className="text-2xl font-bold text-[var(--clr-text-primary)]">{selectedClient.payment_terms || 30}</p>
                  <p className="text-xs text-[var(--clr-text-muted)]">days</p>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-[var(--clr-surface-card)] rounded-lg border shadow-[var(--shadow-card)] p-8 text-center">
              <UserCircle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-[var(--clr-text-primary)]">Select a Client</h3>
              <p className="text-sm text-[var(--clr-text-muted)] mt-2">
                Choose a client from the list to view and edit their billing details
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};


// ===========================
// Client Modal (Create/Edit)
// ===========================

const ClientModal = ({ client, tenants, onSave, onClose }) => {
  const isEditing = !!client;
  const [formData, setFormData] = useState({
    tenant_id: client?.tenant_id || '',
    legal_name: client?.legal_name || '',
    vat_number: client?.vat_number || '',
    registration_number: client?.registration_number || '',
    billing_address: {
      line1: client?.billing_address?.line1 || '',
      line2: client?.billing_address?.line2 || '',
      city: client?.billing_address?.city || '',
      province: client?.billing_address?.province || '',
      postal_code: client?.billing_address?.postal_code || '',
      country: client?.billing_address?.country || 'South Africa'
    },
    contact_email: client?.contact_email || '',
    contact_person: client?.contact_person || '',
    payment_terms: client?.payment_terms || 30
  });

  const handleSubmit = () => {
    if (!formData.legal_name) {
      alert('Client name is required');
      return;
    }
    if (!isEditing && !formData.tenant_id) {
      alert('Please select a tenant');
      return;
    }
    onSave(formData);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/50" onClick={onClose}></div>
      <div className="relative bg-[var(--clr-surface-card)] rounded-xl shadow-xl w-full max-w-2xl p-6 z-10 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
            <UserCircle className="w-6 h-6 text-blue-600" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-[var(--clr-text-primary)]">
              {isEditing ? 'Edit Client' : 'Create New Client'}
            </h2>
            <p className="text-sm text-[var(--clr-text-muted)]">Client billing profile for invoice generation</p>
          </div>
        </div>

        <div className="space-y-6">
          {/* Basic Info */}
          <div className="space-y-4">
            <h3 className="font-medium text-[var(--clr-text-primary)] border-b pb-2">Basic Information</h3>
            
            {!isEditing && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tenant *</label>
                <select
                  value={formData.tenant_id}
                  onChange={(e) => setFormData(prev => ({ ...prev, tenant_id: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  data-testid="client-tenant-select"
                >
                  <option value="">Select tenant...</option>
                  {tenants.map(t => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Legal Name *</label>
              <input
                type="text"
                value={formData.legal_name}
                onChange={(e) => setFormData(prev => ({ ...prev, legal_name: e.target.value }))}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Company Name (Pty) Ltd"
                data-testid="client-name-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">VAT Number</label>
                <input
                  type="text"
                  value={formData.vat_number}
                  onChange={(e) => setFormData(prev => ({ ...prev, vat_number: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="4123456789"
                  data-testid="client-vat-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Registration No.</label>
                <input
                  type="text"
                  value={formData.registration_number}
                  onChange={(e) => setFormData(prev => ({ ...prev, registration_number: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="2020/123456/07"
                  data-testid="client-reg-input"
                />
              </div>
            </div>
          </div>

          {/* Billing Address */}
          <div className="space-y-4">
            <h3 className="font-medium text-[var(--clr-text-primary)] border-b pb-2">Billing Address</h3>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Address Line 1</label>
              <input
                type="text"
                value={formData.billing_address.line1}
                onChange={(e) => setFormData(prev => ({ 
                  ...prev, 
                  billing_address: { ...prev.billing_address, line1: e.target.value }
                }))}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Street Address"
                data-testid="client-addr-line1"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Address Line 2</label>
              <input
                type="text"
                value={formData.billing_address.line2}
                onChange={(e) => setFormData(prev => ({ 
                  ...prev, 
                  billing_address: { ...prev.billing_address, line2: e.target.value }
                }))}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Building, Suite, etc."
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                <input
                  type="text"
                  value={formData.billing_address.city}
                  onChange={(e) => setFormData(prev => ({ 
                    ...prev, 
                    billing_address: { ...prev.billing_address, city: e.target.value }
                  }))}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="City"
                  data-testid="client-city-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Postal Code</label>
                <input
                  type="text"
                  value={formData.billing_address.postal_code}
                  onChange={(e) => setFormData(prev => ({ 
                    ...prev, 
                    billing_address: { ...prev.billing_address, postal_code: e.target.value }
                  }))}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Postal Code"
                  data-testid="client-postal-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Province/State</label>
                <input
                  type="text"
                  value={formData.billing_address.province}
                  onChange={(e) => setFormData(prev => ({ 
                    ...prev, 
                    billing_address: { ...prev.billing_address, province: e.target.value }
                  }))}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Province"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
                <input
                  type="text"
                  value={formData.billing_address.country}
                  onChange={(e) => setFormData(prev => ({ 
                    ...prev, 
                    billing_address: { ...prev.billing_address, country: e.target.value }
                  }))}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Country"
                />
              </div>
            </div>
          </div>

          {/* Contact & Payment */}
          <div className="space-y-4">
            <h3 className="font-medium text-[var(--clr-text-primary)] border-b pb-2">Contact & Payment</h3>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Contact Person</label>
                <input
                  type="text"
                  value={formData.contact_person}
                  onChange={(e) => setFormData(prev => ({ ...prev, contact_person: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="John Smith"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Contact Email</label>
                <input
                  type="email"
                  value={formData.contact_email}
                  onChange={(e) => setFormData(prev => ({ ...prev, contact_email: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="accounts@company.com"
                  data-testid="client-email-input"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Payment Terms (days)</label>
              <input
                type="number"
                value={formData.payment_terms}
                onChange={(e) => setFormData(prev => ({ ...prev, payment_terms: parseInt(e.target.value) || 30 }))}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                min="0"
                data-testid="client-terms-input"
              />
            </div>
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 border rounded-lg hover:bg-[var(--clr-surface-hover)]"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            data-testid="save-client-btn"
          >
            {isEditing ? 'Update Client' : 'Create Client'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BillingAdmin;
